/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Button from 'components/generics/Button/Button';
import { Loader } from 'components/generics/Loader/Loader';
import ChecklistStateBadge from 'components/generics/Checklist/ChecklistStateBadge';

import { getInstance, updateItem } from 'services/API/Checklist';
import { subscribeChecklistEvents } from 'services/API/ChecklistRealtime';

import { formatDate, formatDateAndHour } from 'utils/func';

import './Checklist.style.scss';

const STATUSES = [
  { value: null, label: 'Non renseigné', className: 'checklist-status--empty' },
  { value: 'valid', label: 'Valide', className: 'checklist-status--valid' },
  { value: 'invalid', label: 'Non valide', className: 'checklist-status--invalid' },
];

const errorMessage = err => err?.response?.data?.message || err?.message || 'Unexpected error';

/**
 * Remplissage d'une instance de checklist (modale web).
 * Chaque point est enregistré immédiatement (PATCH) et l'écran se rafraîchit
 * en direct quand un autre utilisateur modifie la même checklist.
 */
const ChecklistFillModal = ({ instanceId, onClose, canEdit = true }) => {
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingItemId, setSavingItemId] = useState(null);
  const mounted = useRef(true);

  const load = () => {
    if (!instanceId) return;
    getInstance(instanceId)
      .then(res => {
        if (mounted.current) setInstance(res.data);
      })
      .catch(err => {
        if (mounted.current) toast.error(errorMessage(err));
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const unsubscribe = subscribeChecklistEvents(event => {
      if (event.type === 'poll' || event.instanceId === Number(instanceId)) {
        load();
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const changeStatus = (item, optionValue) => {
    if (!canEdit || savingItemId) return;
    // Recliquer sur l'état actif remet le point à « non renseigné ».
    const nextStatus = item.status === optionValue ? null : optionValue;
    setSavingItemId(item.id);
    updateItem(instanceId, item.id, nextStatus)
      .then(res => {
        if (!mounted.current) return;
        setInstance(prev => ({
          ...prev,
          state: res.data.state,
          items: prev.items.map(current =>
            current.id === item.id ? { ...current, ...res.data.item } : current,
          ),
        }));
      })
      .catch(err => {
        if (mounted.current) toast.error(errorMessage(err));
      })
      .finally(() => {
        if (mounted.current) setSavingItemId(null);
      });
  };

  if (loading) {
    return (
      <div className="modal__content">
        <Loader />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="modal__content">
        <p>Checklist not found.</p>
        <Button text="Close" defaultStyle="primary" onClick={onClose} />
      </div>
    );
  }

  return (
    <div className="modal__content checklist-fill">
      <h2>{instance.entityLabel}</h2>
      <div className="checklist-fill__meta">
        <span>{formatDate(new Date(instance.date))}</span>
        <span>{instance.templateName || 'Template deleted'}</span>
        <ChecklistStateBadge state={instance.state} />
      </div>
      <ul className="checklist-fill__items">
        {instance.items.map(item => (
          <li key={item.id} className="checklist-fill__item">
            <span className="checklist-fill__label">{item.label}</span>
            <div className="checklist-fill__statuses">
              {STATUSES.map(option => (
                <button
                  type="button"
                  key={String(option.value)}
                  className={`checklist-status ${option.className}${
                    item.status === option.value ? ' checklist-status--active' : ''
                  }`}
                  disabled={!canEdit || savingItemId === item.id}
                  onClick={() => changeStatus(item, option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {item.lastModifiedByEmail ? (
              <span className="checklist-fill__modified">
                {item.lastModifiedByEmail} · {formatDateAndHour(new Date(item.lastModifiedAt))}
              </span>
            ) : (
              <span className="checklist-fill__modified checklist-fill__modified--empty">
                Never modified
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="checklist-fill__footer">
        <Button text="Close" defaultStyle="primary" onClick={onClose} />
      </div>
    </div>
  );
};

export default ChecklistFillModal;
